import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Filter, FilterValue } from "../../Table/Filters";
import { FetchFilters } from "@/utils/utilFunctions/filters";
import { MEDIA_STATUS } from "@/utils/constants/uiConstants";
import { getAllowedTeamIds } from "@/utils/mediautils/mediautils";
import {
  getFilterStorageKey,
  readStoredFilters,
  writeStoredFilters,
} from "@/utils/utilFunctions/filterStorage";

const MULTI_FILTER_KEYS = new Set([
  "lang",
  "category",
  "business",
  "team",
  "agency",
  "ctype",
  "ctby",
  "tags",
  "publishing_destinations",
  "content_used",
]);

function parseFiltersFromSearchParams(
  searchParams: URLSearchParams
): Record<string, FilterValue> {
  const result: Record<string, FilterValue> = {};
  const seen = new Set<string>();
  searchParams.forEach((_, key) => {
    if (seen.has(key) || key === "tab") return;
    seen.add(key);
    const baseKey = key.endsWith("displayValue")
      ? key.slice(0, -"displayValue".length)
      : key;
    if (MULTI_FILTER_KEYS.has(baseKey)) {
      const all = searchParams.getAll(key);
      if (all.length > 0) result[key] = all;
    } else {
      const val = searchParams.get(key);
      if (val != null) {
        try {
          result[key] = JSON.parse(val);
        } catch {
          result[key] = val;
        }
      }
    }
  });
  return result;
}

interface UseDiscoverFiltersProps {
  categoriesConst: any;
  languageConst: any;
  orgBusTeamConstants: any;
  users: any;
  singleCurrentUser: any;
  agencies: any;
  bunchProductList: any;
  bunchBusinessList: any;
  fromMediaList?: boolean;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  getListAgencies: (params: {
    name?: string;
    setInRedux: boolean;
  }) => Promise<any>;
  getListUser: (params: {
    name: string;
    setInRedux: boolean;
    team?: string[];
  }) => Promise<any>;
}

const TabMapping = {
  wired: "wired",
  inhouse: "inhouse",
};

const inhouseFilterObj = {
  is_discover: 1,
  media_status: MEDIA_STATUS.MEDIA_STATUS_PUBLISHED,
};
const wiredFilterObj = { media_status: MEDIA_STATUS.MEDIA_STATUS_PUBLISHED };

export const useDiscoverFilters = ({
  categoriesConst,
  languageConst,
  orgBusTeamConstants,
  users,
  singleCurrentUser,
  agencies,
  bunchProductList,
  bunchBusinessList,
  fromMediaList,
  activeTab,
  setActiveTab,
  getListAgencies,
  getListUser,
}: UseDiscoverFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const requiredFilters = [
    "Media ID",
    "Keyword",
    "Agency",
    "Team/Property",
    "Uploader",
    "Upload Date",
    "Media Duration",
    "Content Used",
  ];
  const [filters, setFilters] = useState<Record<string, FilterValue>>(() => {
    const fromState = (location.state as any)?.preservedFilters as
      | Record<string, FilterValue>
      | undefined;
    if (fromState && Object.keys(fromState).length > 0) return fromState;
    const stored = readStoredFilters();
    if (Object.keys(stored).length > 0) return stored;
    return parseFiltersFromSearchParams(searchParams);
  });
  const [availableFilters, setAvailableFilters] = useState<Filter[]>([]);
  const [hiddenFilter, setHiddenFilter] = useState<Record<string, any>>(() => {
    const pathname = window.location.pathname;
    const assetTypeFromPath = pathname.split("/").pop();
    const tabParam = searchParams.get("tab");

    const initialActiveType =
      tabParam ||
      (Object.keys(TabMapping).includes(assetTypeFromPath || "")
        ? assetTypeFromPath
        : "wired");

    if (initialActiveType === "inhouse") {
      return inhouseFilterObj;
    }
    return wiredFilterObj;
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [autoselectAgency, setAutoselectAgency] = useState<any[]>([]);
  const [agencyInitialOptions, setAgencyInitialOptions] = useState<any[]>([]);
  const [autoselectedUploader, setAutoselectedUploader] = useState<any[]>([]);

  // Update hiddenFilter when activeTab changes
  useEffect(() => {
    if (!singleCurrentUser || !singleCurrentUser.team || fromMediaList) return;

    if (activeTab === "inhouse") {
      setHiddenFilter(inhouseFilterObj);
    } else {
      setHiddenFilter(wiredFilterObj);
    }
  }, [fromMediaList, singleCurrentUser, activeTab]);

  // Set available filters
  useEffect(() => {
    setAvailableFilters(
      FetchFilters(
        orgBusTeamConstants,
        categoriesConst,
        languageConst,
        users,
        singleCurrentUser,
        bunchProductList,
        agencies,
        bunchBusinessList,
        "discover",
        requiredFilters
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categoriesConst,
    languageConst,
    orgBusTeamConstants,
    users,
    singleCurrentUser,
    bunchBusinessList,
    bunchProductList,
    agencies,
  ]);

  // Pre-fetch top agencies once on mount for the quick-chip row in FilterSheet.
  useEffect(() => {
    getListAgencies({ setInRedux: false }).then((res: any) => {
      const agencies = res || [];
      if (agencies.length > 0) setAgencyInitialOptions(agencies);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ids = filters.agency;
    const labels = filters.agencydisplayValue;
    const idArr = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
    const labelArr = Array.isArray(labels)
      ? labels
      : labels != null
        ? [labels]
        : [];

    if (idArr.length > 0) {
      setAutoselectAgency(
        idArr.map((id, index) => ({
          name: labelArr[index] ?? String(id),
          id: String(id),
        }))
      );
    } else {
      setAutoselectAgency([]);
    }
  }, [filters]);

  useEffect(() => {
    writeStoredFilters(filters, getFilterStorageKey());
    setSearchParams(
      (prev) => {
        const tab = prev.get("tab");
        return tab ? { tab } : {};
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleSetFilters = (filters: Record<string, FilterValue>) => {
    setFilters(filters);
  };

  const handleAgencyFilterChange = (value: string) => {
    setActiveTab(value);
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters.page;
      return newFilters;
    });
  };

  const handleHiddenFilter = (e: any, filter: any) => {
    e.preventDefault();
    handleAgencyFilterChange(filter);
    const filtersWithoutPage: Record<string, FilterValue> = { ...filters };
    delete filtersWithoutPage.page;
    navigate(`/discover/${filter}`, {
      state: { preservedFilters: filtersWithoutPage },
    });
  };

  const onAutoSelectInputChange = (params: any) => {
    const keyword = (params.keyword || "").trim();
    if (keyword.length >= 3) {
      if (params.name === "agency") {
        getListAgencies({ name: keyword, setInRedux: false }).then(
          (res: any) => {
            setAutoselectAgency(res || []);
          }
        );
      } else if (params.name === "ctby") {
        const allTeamIds = getAllowedTeamIds(singleCurrentUser?.permissions);
        const specificTeamIds = allTeamIds.filter((id) => id !== "*");
        const userSearchParams: {
          name: string;
          setInRedux: boolean;
          team?: string[];
        } = {
          name: keyword,
          setInRedux: false,
        };
        if (specificTeamIds.length > 0) {
          userSearchParams.team = specificTeamIds;
        }
        getListUser(userSearchParams).then((res: any) => {
          setAutoselectedUploader(res || []);
        });
      }
    } else {
      if (params.name === "agency") {
        setAutoselectAgency([]);
      } else if (params.name === "ctby") {
        setAutoselectedUploader([]);
      }
    }
  };

  const autoSelectOptionsMap = {
    agency: (autoselectAgency || []).map((item: any) => ({
      label: item?.name,
      value: String(item?.id),
    })),
    agencyQuick: (agencyInitialOptions || []).map((item: any) => ({
      label: item?.name,
      value: String(item?.id),
    })),
    ctby: (autoselectedUploader || []).map((item: any) => ({
      label: item?.name + (item?.email ? " (" + item?.email + ")" : ""),
      value: String(item?.id),
    })),
  };

  return {
    filters,
    setFilters,
    handleSetFilters,
    availableFilters,
    hiddenFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    autoselectAgency,
    handleAgencyFilterChange,
    handleHiddenFilter,
    onAutoSelectInputChange,
    autoSelectOptionsMap,
  };
};
